import { useEffect, useMemo, useState } from "react";
import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import {
  Tabs,
  TabsContent,
  TabsTrigger,
} from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

export type TheoryTabContentModel = {
  written: { title: string; description: string };
  oral: { currentVerse: number };
  practical: { title: string; content: string } | null;
  additionalTheory?: { title: string; content: string }[];
};

export type OralVerseModel = {
  verse: number;
  title: string;
  content: string;
  type:
    | "student_creed"
    | "korean_count"
    | "black_belt_cycle"
    | "tenets"
    | "black_belt_oath";
};

type SubTab = { value: string; label: string };

/** Matches `BreakingTechniques` / `getBreakingTechniques` in MembersArea. */
export type BreakingContentModel = {
  hand?: string;
  foot?: string;
  creative?: string;
};

interface CurriculumNonCombatTheoryProps {
  /** Used to reset sub-tabs when the belt changes. */
  selectedBelt: string;
  theoryContent: TheoryTabContentModel;
  applicableOralVerses: OralVerseModel[];
  /** Same classes as main curriculum tab strip (`getCurriculumTabBeltSurface`). */
  beltTabSurfaceClass: string;
}

function buildTheorySubTabs(
  theoryContent: TheoryTabContentModel,
  applicableOralVerses: OralVerseModel[],
): SubTab[] {
  const cv = theoryContent.oral.currentVerse;
  const tabs: SubTab[] = [];

  if (theoryContent.written.title.trim()) {
    tabs.push({ value: "essays", label: "Essays" });
  }

  if (applicableOralVerses.some((v) => v.type === "student_creed")) {
    tabs.push({ value: "student-creed", label: "Student Creed" });
  }

  if (cv >= 4) tabs.push({ value: "korean", label: "Korean" });
  if (cv >= 5) tabs.push({ value: "bb-success-cycle", label: "BB Success Cycle" });
  if (cv >= 6) tabs.push({ value: "tenets", label: "Tenets" });
  if (cv >= 7) tabs.push({ value: "bb-oath", label: "BB Oath" });

  if (theoryContent.additionalTheory && theoryContent.additionalTheory.length > 0) {
    tabs.push({ value: "additional", label: "Additional" });
  }

  return tabs;
}

/** Shown under Curriculum → Sparring/Breaking (Breaking sub-tab). */
export function BreakingTabBody({
  selectedBelt,
  breaking,
}: {
  selectedBelt: string;
  breaking: BreakingContentModel;
}) {
  const blackBeltsCreativeOnly = [
    "BLACK/WHITE",
    "BLACK/D1",
    "BLACK/D2",
    "BLACK/D3",
    "BLACK/D4",
    "BLACK/D5",
  ].includes(selectedBelt);

  return (
    <div className="pt-2">
      {blackBeltsCreativeOnly ? (
        <div className="rounded-lg bg-gray-50 p-4">
          <p className="text-gray-700">{breaking.creative}</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <h4 className="mb-2 font-medium text-base text-taekwondo-red">Hand Breaking</h4>
            <div className="rounded-lg bg-gray-50 p-4">
              <p className="text-gray-700">
                {breaking.hand || "No hand breaking required for this belt level."}
              </p>
            </div>
          </div>
          <div>
            <h4 className="mb-2 font-medium text-base text-taekwondo-red">Foot Breaking</h4>
            <div className="rounded-lg bg-gray-50 p-4">
              <p className="text-gray-700">
                {breaking.foot || "No foot breaking required for this belt level."}
              </p>
            </div>
          </div>
          {selectedBelt === "NAVY" && (
            <div>
              <h4 className="mb-2 font-medium text-base text-taekwondo-red">Creative Breaking</h4>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-gray-700">{breaking.creative}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CurriculumNonCombatTheory({
  selectedBelt,
  theoryContent,
  applicableOralVerses,
  beltTabSurfaceClass,
}: CurriculumNonCombatTheoryProps) {
  const subTabs = useMemo(
    () => buildTheorySubTabs(theoryContent, applicableOralVerses),
    [theoryContent, applicableOralVerses],
  );

  const [activeSubTab, setActiveSubTab] = useState(
    () => subTabs[0]?.value ?? "",
  );

  useEffect(() => {
    if (subTabs.length === 0) return;
    setActiveSubTab((prev) =>
      subTabs.some((t) => t.value === prev) ? prev : subTabs[0].value,
    );
  }, [selectedBelt, subTabs]);

  const studentCreedVerses = useMemo(
    () =>
      [...applicableOralVerses]
        .filter((v) => v.type === "student_creed")
        .sort((a, b) => a.verse - b.verse),
    [applicableOralVerses],
  );

  const koreanVerse = applicableOralVerses.find((v) => v.verse === 4);
  const bbSuccessVerse = applicableOralVerses.find((v) => v.verse === 5);
  const tenetsVerse = applicableOralVerses.find((v) => v.verse === 6);
  const oathVerse = applicableOralVerses.find((v) => v.verse === 7);

  return (
    <div className="space-y-6 text-left">
      {theoryContent.practical && (
        <section className="rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5">
        
          <h3 className="mb-2 font-medium text-base text-gray-900 text-taekwondo-red">
            {theoryContent.practical.title}
          </h3>
          <p className="whitespace-pre-line text-gray-700">
            {theoryContent.practical.content}
          </p>
        </section>
      )}

      {subTabs.length === 0 ? (
        <p className="text-center text-sm text-gray-600">
          No theory requirements are configured for this belt.
        </p>
      ) : (
        <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
          <ScrollableCurriculumTabStrip
            key={subTabs.map((t) => t.value).join(",")}
            ariaLabelPrefix="Theory and other sub-tabs"
          >
            {subTabs.map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                className={cn(
                  "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium",
                  "border border-black/10 transition-shadow",
                  beltTabSurfaceClass,
                  "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/35 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
                )}
              >
                {t.label}
              </TabsTrigger>
            ))}
          </ScrollableCurriculumTabStrip>

          {subTabs.some((t) => t.value === "essays") && (
            <TabsContent value="essays" className="mt-4 focus-visible:outline-none">
              <h3 className="mb-2 font-semibold text-lg text-taekwondo-red">
                Written theory (essay)
              </h3>
              <div className="rounded-lg bg-gray-50 p-4">
                <h4 className="mb-2 font-medium text-base text-gray-900">
                  {theoryContent.written.title}
                </h4>
                <p className="text-gray-700">{theoryContent.written.description}</p>
              </div>
            </TabsContent>
          )}

          {subTabs.some((t) => t.value === "student-creed") && (
            <TabsContent
              value="student-creed"
              className="mt-4 focus-visible:outline-none"
            >
              <h3 className="mb-3 font-semibold text-lg text-taekwondo-red">
                Student Creed
              </h3>
              <ul className="space-y-4">
                {studentCreedVerses.map((v) => (
                  <li
                    key={v.verse}
                    className="rounded-lg border border-gray-100 bg-gray-50/80 p-4"
                  >
                    <p className="mb-2 font-medium text-taekwondo-red">
                      {v.verse}. {v.title}
                    </p>
                    <p className="whitespace-pre-line text-gray-700">{v.content}</p>
                  </li>
                ))}
              </ul>
            </TabsContent>
          )}

          {subTabs.some((t) => t.value === "korean") && koreanVerse && (
            <TabsContent value="korean" className="mt-4 focus-visible:outline-none">
              <h3 className="mb-2 font-semibold text-lg text-taekwondo-red">
                {koreanVerse.title}
              </h3>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="whitespace-pre-line text-gray-700">
                  {koreanVerse.content}
                </p>
              </div>
            </TabsContent>
          )}

          {subTabs.some((t) => t.value === "bb-success-cycle") && bbSuccessVerse && (
            <TabsContent
              value="bb-success-cycle"
              className="mt-4 focus-visible:outline-none"
            >
              <h3 className="mb-2 font-semibold text-lg text-taekwondo-red">
                {bbSuccessVerse.title}
              </h3>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="whitespace-pre-line text-gray-700">
                  {bbSuccessVerse.content}
                </p>
              </div>
            </TabsContent>
          )}

          {subTabs.some((t) => t.value === "tenets") && tenetsVerse && (
            <TabsContent value="tenets" className="mt-4 focus-visible:outline-none">
              <h3 className="mb-2 font-semibold text-lg text-taekwondo-red">
                {tenetsVerse.title}
              </h3>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="whitespace-pre-line text-gray-700">
                  {tenetsVerse.content}
                </p>
              </div>
            </TabsContent>
          )}

          {subTabs.some((t) => t.value === "bb-oath") && oathVerse && (
            <TabsContent value="bb-oath" className="mt-4 focus-visible:outline-none">
              <h3 className="mb-2 font-semibold text-lg text-taekwondo-red">
                {oathVerse.title}
              </h3>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="whitespace-pre-line text-gray-700">{oathVerse.content}</p>
              </div>
            </TabsContent>
          )}

          {subTabs.some((t) => t.value === "additional") &&
            theoryContent.additionalTheory && (
              <TabsContent value="additional" className="mt-4 focus-visible:outline-none">
                <h3 className="mb-3 font-semibold text-lg text-taekwondo-red">
                  Additional theory requirements
                </h3>
                <ul className="space-y-4">
                  {theoryContent.additionalTheory.map((item, index) => (
                    <li
                      key={`${item.title}-${index}`}
                      className="rounded-lg border border-gray-100 bg-gray-50/80 p-4"
                    >
                      <h4 className="mb-2 font-medium text-gray-900">{item.title}</h4>
                      <p className="whitespace-pre-line text-gray-700">{item.content}</p>
                    </li>
                  ))}
                </ul>
              </TabsContent>
            )}
        </Tabs>
      )}
    </div>
  );
}
